import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { AlertTriangle, CheckCircle, ExternalLink, FileText, Globe, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  setFinanceDashboardViewCrossBorderCardTestId,
  setFinanceDashboardViewCrossBorderSummaryTestId,
  setFinanceDashboardViewDescriptionTestId,
  setFinanceDashboardViewExceptionsCardTestId,
  setFinanceDashboardViewPaidCardTestId,
  setFinanceDashboardViewPaymentTrendChartTestId,
  setFinanceDashboardViewStatusDistributionChartTestId,
  setFinanceDashboardViewTaxComplianceTestId,
  setFinanceDashboardViewTestId,
  setFinanceDashboardViewTitleTestId,
  setFinanceDashboardViewTotalInvoicesCardTestId,
  setFinanceDashboardViewUnpaidOverdueCardTestId,
  setFinanceDashboardViewViewAllButtonTestId,
  setFinanceDashboardViewWorklistTestId,
} from "./finance-dashboard-view.utils";

// DashBoardInvoice type specific to finance dashboard (different from PurchaseOrderInvoice used in invoices view)
interface DashBoardInvoice {
  id: string;
  status: "Paid" | "Unpaid" | "Overdue";
  amountEUR: number;
  country: string;
  dueDate: string;
  tax?: {
    vatId?: string;
  };
}

interface PaymentTrendDataPoint {
  month: string;
  invoices: number;
  payments: number;
}

interface FinanceDashboardViewProps {
  paymentTrendData: PaymentTrendDataPoint[];
  testId?: string;
}

// Subcomponents
interface MetricCardProps {
  title: string;
  icon: React.ReactNode;
  value: number;
  amount?: number;
  badge?: React.ReactNode;
  onClick?: () => void;
}

function MetricCard({ title, icon, value, amount, badge, onClick }: MetricCardProps) {
  return (
    <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={onClick}>
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {amount !== undefined && (
          <p className="text-xs text-muted-foreground mt-1">€{amount.toLocaleString("de-DE", { minimumFractionDigits: 2 })}</p>
        )}
        {badge}
      </CardContent>
    </Card>
  );
}

interface WorklistItemProps {
  item: DashBoardInvoice & { issue: string; severity: "high" | "medium" };
  onAction: (invoiceId: string) => void;
}

function WorklistItem({ item, onAction }: WorklistItemProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors">
      <div className="flex items-center gap-4 flex-1">
        <div className="flex flex-col">
          <div className="font-semibold">{item.id}</div>
          <div className="text-sm text-muted-foreground">
            {item.country} • {t("views.financeDashboard.worklist.due")}: {new Date(item.dueDate).toLocaleDateString("de-DE")}
          </div>
        </div>
        <Badge variant={item.severity === "high" ? "destructive" : "secondary"}>{item.issue}</Badge>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <div className="font-bold">
            €
            {item.amountEUR.toLocaleString("de-DE", {
              minimumFractionDigits: 2,
            })}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => onAction(item.id)}>
          {t("views.financeDashboard.worklist.open")}
        </Button>
      </div>
    </div>
  );
}

interface CrossBorderSummaryRowProps {
  label: string;
  value: number;
}

function CrossBorderSummaryRow({ label, value }: CrossBorderSummaryRowProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
      <div className="flex items-center gap-2">
        <div className="font-semibold">{label}</div>
      </div>
      <div className="text-right">
        <div className="font-bold">{value}</div>
        <div className="text-xs text-muted-foreground">{t("views.financeDashboard.invoices")}</div>
      </div>
    </div>
  );
}

interface TaxComplianceRowProps {
  country: string;
  subtitle: string;
  invoiceCount: number;
  compliancePercent: number;
}

function TaxComplianceRow({ country, subtitle: _subtitle, invoiceCount, compliancePercent }: TaxComplianceRowProps) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
      <div>
        <div className="font-semibold">{country}</div>
        <div className="text-xs text-muted-foreground">{invoiceCount} invoices</div>
      </div>
      <div className="flex items-center gap-2">
        <div className="text-2xl font-bold">{compliancePercent}%</div>
        {compliancePercent >= 95 ? (
          <CheckCircle className="h-5 w-5 text-green-600" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-orange-600" />
        )}
      </div>
    </div>
  );
}

export function FinanceDashboardView({ paymentTrendData, testId }: FinanceDashboardViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<DashBoardInvoice[]>([]);
  const [, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("this-month");
  const [selectedCountry, setSelectedCountry] = useState<"ALL" | "DE" | "NL">("ALL");

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCountry !== "ALL") params.append("country", selectedCountry);

      const response = await fetch(`/api/invoices?${params.toString()}`);
      const data = await response.json();
      setInvoices(data.invoices || []);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedCountry]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetchInvoices toggles loading state internally; effect fires on mount and when fetcher identity changes
    void fetchInvoices();
  }, [fetchInvoices]);

  // Calculate summary metrics
  const totalInvoices = invoices.length;
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amountEUR, 0);
  const paidInvoices = invoices.filter((inv) => inv.status === "Paid");
  const paidAmount = paidInvoices.reduce((sum, inv) => sum + inv.amountEUR, 0);
  const unpaidInvoices = invoices.filter((inv) => inv.status === "Unpaid");
  const unpaidAmount = unpaidInvoices.reduce((sum, inv) => sum + inv.amountEUR, 0);
  const overdueInvoices = invoices.filter((inv) => inv.status === "Overdue");
  const overdueAmount = overdueInvoices.reduce((sum, inv) => sum + inv.amountEUR, 0);
  const crossBorderInvoices = invoices.filter((inv) => inv.country !== "DE" && inv.country !== "NL");
  const crossBorderValidVat = crossBorderInvoices.filter((inv) => inv.tax?.vatId).length;
  const crossBorderVatPercent = crossBorderInvoices.length > 0 ? Math.round((crossBorderValidVat / crossBorderInvoices.length) * 100) : 100;

  // Chart data
  const statusChartData = [
    { name: "Paid", value: paidInvoices.length, color: "#00A86B" },
    { name: "Unpaid", value: unpaidInvoices.length, color: "#FFA500" },
    { name: "Overdue", value: overdueInvoices.length, color: "#E53935" },
  ].filter((item) => item.value > 0);

  // Cross-border summary
  const deTonl = invoices.filter((inv) => inv.country === "DE").length;
  const nlTode = invoices.filter((inv) => inv.country === "NL").length;

  // Tax compliance
  const deInvoices = invoices.filter((inv) => inv.country === "DE");
  const deValidVat = deInvoices.filter((inv) => inv.tax?.vatId && /^DE[0-9]{9}$/.test(inv.tax.vatId)).length;
  const deCompliance = deInvoices.length > 0 ? Math.round((deValidVat / deInvoices.length) * 100) : 0;

  const nlInvoices = invoices.filter((inv) => inv.country === "NL");
  const nlValidVat = nlInvoices.filter((inv) => inv.tax?.vatId && /^NL[0-9]{9}B[0-9]{2}$/.test(inv.tax.vatId)).length;
  const nlCompliance = nlInvoices.length > 0 ? Math.round((nlValidVat / nlInvoices.length) * 100) : 0;

  // Worklist items
  const worklistItems = [
    ...overdueInvoices.slice(0, 5).map((inv) => ({
      ...inv,
      issue: "Overdue",
      severity: "high" as const,
    })),
    ...invoices
      .filter((inv) => !inv.tax?.vatId)
      .slice(0, 3)
      .map((inv) => ({
        ...inv,
        issue: "Missing VAT",
        severity: "medium" as const,
      })),
  ];

  const handleCardClick = (filter: string) => {
    navigate(`${ROUTE_PATHS.FINANCE_INVOICES}?status=${filter}`);
  };

  const handleChartClick = (status: string) => {
    navigate(`${ROUTE_PATHS.FINANCE_INVOICES}?status=${status.toLowerCase()}`);
  };

  const handleWorklistAction = (invoiceId: string) => {
    navigate(`${ROUTE_PATHS.FINANCE_INVOICES}?invoiceId=${invoiceId}`);
  };

  // Mock user for finance role check (since auth context might not have financeRole)
  const userFinanceRole = "finance-ops";

  return (
    <div data-testid={testId ?? setFinanceDashboardViewTestId("container")} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid={setFinanceDashboardViewTitleTestId()} className="text-2xl font-semibold tracking-tight text-balance">
            {t("views.financeDashboard.title")}
          </h1>
          <p data-testid={setFinanceDashboardViewDescriptionTestId()} className="mt-1 text-sm text-muted-foreground">
            {t("views.financeDashboard.description")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this-month">{t("views.financeDashboard.filters.thisMonth")}</SelectItem>
              <SelectItem value="last-month">{t("views.financeDashboard.filters.lastMonth")}</SelectItem>
              <SelectItem value="this-quarter">{t("views.financeDashboard.filters.thisQuarter")}</SelectItem>
              <SelectItem value="this-year">{t("views.financeDashboard.filters.thisYear")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedCountry} onValueChange={(val: "ALL" | "DE" | "NL") => setSelectedCountry(val)}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t("views.financeDashboard.filters.allCountries")}</SelectItem>
              <SelectItem value="DE">{t("views.financeDashboard.filters.germany")}</SelectItem>
              <SelectItem value="NL">{t("views.financeDashboard.filters.netherlands")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 1: Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <div data-testid={setFinanceDashboardViewTotalInvoicesCardTestId()}>
          <MetricCard
            title={t("views.financeDashboard.cards.totalInvoices")}
            icon={<FileText className="h-4 w-4 text-muted-foreground" />}
            value={totalInvoices}
            amount={totalAmount}
            onClick={() => navigate(ROUTE_PATHS.FINANCE_INVOICES)}
          />
        </div>

        <div data-testid={setFinanceDashboardViewPaidCardTestId()}>
          <MetricCard
            title={t("views.financeDashboard.cards.paid")}
            icon={<CheckCircle className="h-4 w-4 text-green-600" />}
            value={paidInvoices.length}
            amount={paidAmount}
            onClick={() => handleCardClick("paid")}
          />
        </div>

        <div data-testid={setFinanceDashboardViewUnpaidOverdueCardTestId()}>
          <MetricCard
            title={t("views.financeDashboard.cards.unpaidOverdue")}
            icon={<XCircle className="h-4 w-4 text-orange-600" />}
            value={unpaidInvoices.length + overdueInvoices.length}
            amount={unpaidAmount + overdueAmount}
            badge={
              overdueAmount > 5000 && (
                <Badge variant="destructive" className="mt-2 text-xs">
                  {t("views.financeDashboard.badges.highRisk")}
                </Badge>
              )
            }
            onClick={() => handleCardClick("unpaid,overdue")}
          />
        </div>

        <Card data-testid={setFinanceDashboardViewCrossBorderCardTestId()} className="cursor-pointer hover:bg-accent/50 transition-colors">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">{t("views.financeDashboard.cards.crossBorder")}</CardTitle>
            <Globe className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{crossBorderInvoices.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {crossBorderVatPercent}% {t("views.financeDashboard.vatValid")}
            </p>
          </CardContent>
        </Card>

        {userFinanceRole === "finance-ops" && (
          <div data-testid={setFinanceDashboardViewExceptionsCardTestId()}>
            <MetricCard
              title={t("views.financeDashboard.cards.exceptions")}
              icon={<AlertTriangle className="h-4 w-4 text-red-600" />}
              value={worklistItems.length}
              badge={<p className="text-xs text-muted-foreground mt-1">{t("views.financeDashboard.badges.requiresAction")}</p>}
              onClick={() => navigate(`${ROUTE_PATHS.FINANCE_INVOICES}?hasIssues=true`)}
            />
          </div>
        )}
      </div>

      {/* Row 2: Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card data-testid={setFinanceDashboardViewStatusDistributionChartTestId()}>
          <CardHeader>
            <CardTitle>{t("views.financeDashboard.charts.statusDistribution")}</CardTitle>
            <CardDescription>{t("views.financeDashboard.charts.clickSegments")}</CardDescription>
          </CardHeader>
          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${percent ? (percent * 100).toFixed(0) : 0}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  onClick={(data) => handleChartClick(data.name ?? "")}
                  className="cursor-pointer"
                >
                  {statusChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid={setFinanceDashboardViewPaymentTrendChartTestId()}>
          <CardHeader>
            <CardTitle>{t("views.financeDashboard.charts.paymentTrend")}</CardTitle>
            <CardDescription>{t("views.financeDashboard.charts.invoicesVsPayments")}</CardDescription>
          </CardHeader>
          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={paymentTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="invoices" stroke="#007AFF" strokeWidth={2} name="Invoices" />
                <Line type="monotone" dataKey="payments" stroke="#00A86B" strokeWidth={2} name="Payments" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Cross-Border & Tax Compliance */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card data-testid={setFinanceDashboardViewCrossBorderSummaryTestId()}>
          <CardHeader>
            <CardTitle>{t("views.financeDashboard.crossBorder.title")}</CardTitle>
            <CardDescription>{t("views.financeDashboard.crossBorder.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <CrossBorderSummaryRow label="DE → NL" value={deTonl} />
              <CrossBorderSummaryRow label="NL → DE" value={nlTode} />
            </div>
          </CardContent>
        </Card>

        <Card data-testid={setFinanceDashboardViewTaxComplianceTestId()}>
          <CardHeader>
            <CardTitle>{t("views.financeDashboard.taxCompliance.title")}</CardTitle>
            <CardDescription>{t("views.financeDashboard.taxCompliance.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <TaxComplianceRow
                country={t("views.financeDashboard.taxCompliance.germany")}
                subtitle=""
                invoiceCount={deInvoices.length}
                compliancePercent={deCompliance}
              />
              <TaxComplianceRow
                country={t("views.financeDashboard.taxCompliance.netherlands")}
                subtitle=""
                invoiceCount={nlInvoices.length}
                compliancePercent={nlCompliance}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Worklist */}
      <Card data-testid={setFinanceDashboardViewWorklistTestId()}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("views.financeDashboard.worklist.title")}</CardTitle>
              <CardDescription>{t("views.financeDashboard.worklist.description")}</CardDescription>
            </div>
            <Button
              data-testid={setFinanceDashboardViewViewAllButtonTestId()}
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTE_PATHS.FINANCE_INVOICES)}
            >
              {t("views.financeDashboard.worklist.viewAll")}
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {worklistItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-600" />
              <p>{t("views.financeDashboard.worklist.allCaughtUp")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {worklistItems.map((item) => (
                <WorklistItem key={item.id} item={item} onAction={handleWorklistAction} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
